import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { InboxDetailPage } from "../../../../components/dashboard/inbox";
import { parseInboxDirection } from "@/features/inbox/filters";
import {
  deleteThreadMutationOptions,
  inboxThreadQueryKey,
  inboxThreadQueryOptions,
  markThreadReadMutationOptions,
  markThreadUnreadMutationOptions,
} from "@/features/inbox/queries";

export const Route = createFileRoute("/dashboard/inbox/$id")({
  validateSearch: (search: Record<string, unknown>) => {
    const includeBounce = search.includeBounce === true || search.includeBounce === "true";
    const includeWarmup = search.includeWarmup === true || search.includeWarmup === "true";

    return {
      domainId: (search.domainId as string) || undefined,
      aliasId: (search.aliasId as string) || undefined,
      direction: parseInboxDirection(search.direction),
      q: (search.q as string) || undefined,
      ...(includeBounce ? { includeBounce: true } : {}),
      ...(includeWarmup ? { includeWarmup: true } : {}),
    };
  },
  head: () => ({
    meta: [{ title: "Conversation - Bavimail" }],
  }),
  staleTime: 15_000,
  preloadStaleTime: 15_000,
  loader: async ({ params, context }) => {
    await context.queryClient.ensureQueryData(inboxThreadQueryOptions(params.id));
  },
  component: DashboardInboxDetailRoute,
});

function DashboardInboxDetailRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = Route.useParams();
  const { domainId, aliasId, direction, q, includeBounce, includeWarmup } = Route.useSearch();
  const { data: thread } = useSuspenseQuery(inboxThreadQueryOptions(id));
  const markReadMutation = useMutation(markThreadReadMutationOptions(queryClient, id));
  const markUnreadMutation = useMutation(markThreadUnreadMutationOptions(queryClient, id));
  const deleteMutation = useMutation(deleteThreadMutationOptions(queryClient, id));
  const autoReadThreadIdRef = useRef<string | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (autoReadThreadIdRef.current === thread.id) return;
    autoReadThreadIdRef.current = thread.id;
    if (!thread.hasUnread) return;
    if (deleteMutation.isPending) return;
    if (markReadMutation.isPending) return;
    markReadMutation.mutate();
  }, [deleteMutation.isPending, markReadMutation, thread.hasUnread, thread.id]);

  return (
    <>
      <InboxDetailPage
        thread={thread}
        selectedDomainId={domainId}
        selectedAliasId={aliasId}
        activeDirection={direction}
        includeBounce={includeBounce}
        includeWarmup={includeWarmup}
        searchQuery={q}
        markingUnread={markUnreadMutation.isPending}
        deletingConversation={deleteMutation.isPending}
        deleteErrorMessage={deleteErrorMessage}
        onMarkUnread={async () => {
          await markUnreadMutation.mutateAsync();
        }}
        onDeleteConversation={async () => {
          try {
            setDeleteErrorMessage(null);
            await deleteMutation.mutateAsync();
            queryClient.removeQueries({ queryKey: inboxThreadQueryKey(id), exact: true });
            await navigate({
              to: "/dashboard/inbox",
              search: { domainId, aliasId, direction, q, includeBounce, includeWarmup },
            });
          } catch (error) {
            setDeleteErrorMessage(
              error instanceof Error ? error.message : "Failed to delete the conversation.",
            );
          }
        }}
      />
      <Outlet />
    </>
  );
}
